// AppSync Events WebSocket client for CRA.
// Auth: AMAZON_COGNITO_USER_POOLS (uses idToken from login).

type AnyObj = Record<string, any>;
export const APPSYNC_EVENTS_WS_DEBUG = false;

function env(name: string): string {
  const v = process.env[name];
  if (!v) throw new Error(`[appsync-events] Missing env ${name}`);
  return v;
}

function toHost(raw: string): string {
  const v = (raw || '').trim();
  if (!v) return '';
  const withScheme = /^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//.test(v) ? v : `https://${v}`;
  try {
    return new URL(withScheme).host;
  } catch {
    return v
      .replace(/^wss?:\/\//i, '')
      .replace(/^https?:\/\//i, '')
      .replace(/\/+.*$/, '');
  }
}

function normalizeChannelPath(path: string): string {
  const p = (path || '').trim();
  if (!p) throw new Error('[appsync-events] Missing channelPath');
  return p.startsWith('/') ? p : `/${p}`;
}

function base64UrlEncode(input: string): string {
  // base64 -> base64url (without padding)
  const b64 = btoa(unescape(encodeURIComponent(input)));
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function base64UrlDecode(input: string): string {
  const normalized = input.replace(/-/g, '+').replace(/_/g, '/');
  const paddingNeeded = (4 - (normalized.length % 4)) % 4;
  return atob(normalized + '='.repeat(paddingNeeded));
}

export function getSubFromIdToken(token: string): string | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = base64UrlDecode(payload);
    const data = JSON.parse(json);
    return data?.sub ?? null;
  } catch {
    return null;
  }
}

function uuid(): string {
  // suficiente para ids internos
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export type SubscribeParams = {
  channelPath: string; // ej: "/notifications/user/<sub>"
  onConnected?: () => void;
  onEvent: (evt: any) => void;
  onError?: (err: any) => void;
  debug?: boolean;
};

export function subscribeUserNotificationsWS(params: SubscribeParams) {
  const httpHost = toHost(env('REACT_APP_APPSYNC_EVENTS_HTTP_DOMAIN'));
  const realtimeHost = toHost(env('REACT_APP_APPSYNC_EVENTS_REALTIME_DOMAIN'));
  const channelPath = normalizeChannelPath(params.channelPath);
  const apiKey = (process.env.REACT_APP_APPSYNC_EVENTS_API_KEY || '').trim();
  let alive = true;
  let ws: WebSocket | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let retryAttempt = 0;
  let reconnectScheduled = false;

  const log = (...a: any[]) => {
    if (APPSYNC_EVENTS_WS_DEBUG || params.debug) console.log('[appsync-events]', ...a);
  };

  const safeError = (e: any) => {
    try {
      params.onError?.(e);
    } catch {}
  };

  const clearRetry = () => {
    reconnectScheduled = false;
    if (retryTimer) clearTimeout(retryTimer);
    retryTimer = null;
  };

  const closeSocket = (reason: string) => {
    const current = ws;
    ws = null;
    if (!current) return;
    try {
      current.onopen = null;
      current.onmessage = null;
      current.onerror = null;
      current.onclose = null;
      current.close(1000, reason);
    } catch {}
  };

  const scheduleReconnect = (reason: string, details?: any) => {
    if (!alive || reconnectScheduled) return;
    reconnectScheduled = true;
    retryAttempt += 1;
    const base = Math.min(30000, 500 * Math.pow(2, retryAttempt));
    const jitter = Math.floor(Math.random() * 300);
    const delay = base + jitter;
    log('reconnect scheduled', { reason, delay, retryAttempt, details });
    retryTimer = setTimeout(() => {
      retryTimer = null;
      reconnectScheduled = false;
      if (!alive) return;
      connect();
    }, delay);
  };

  const connect = () => {
    if (!alive) return;

    const idToken = localStorage.getItem('idToken') || '';
    const hasCognitoId = !!idToken && !!getSubFromIdToken(idToken);
    if (!hasCognitoId) {
      safeError(new Error('[appsync-events] Missing auth: idToken is required'));
      scheduleReconnect('missing_id_token');
      return;
    }
    if (!apiKey) {
      safeError(new Error('[appsync-events] Missing auth: API key is required for connection'));
      return;
    }

    closeSocket('reconnect');
    clearRetry();

    const subscriptionId = uuid();
    let gotConnectionAck = false;
    let gotSubscribeSuccess = false;

    const handshakeHeaderObj = {
      host: httpHost,
      'x-api-key': apiKey
    };
    const headerProto = `header-${base64UrlEncode(JSON.stringify(handshakeHeaderObj))}`;
    const wsUrl = `wss://${realtimeHost}/event/realtime`;
    const socket = new WebSocket(wsUrl, [headerProto, 'aws-appsync-event-ws']);
    ws = socket;

    socket.onopen = () => {
      if (ws !== socket || !alive) return;
      retryAttempt = 0;
      log('open', { wsUrl, channel: channelPath, mode: 'apiKey' });
      if (socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type: 'connection_init' }));
      }
    };

    socket.onmessage = (raw) => {
      if (ws !== socket || !alive) return;
      let msg: AnyObj;
      try {
        msg = JSON.parse(String(raw.data));
      } catch {
        return;
      }
      if (!msg || typeof msg.type !== 'string') return;

      log('<-', msg);

      if (msg.type === 'connection_ack') {
        gotConnectionAck = true;
        params.onConnected?.();

        const subscribeMsg = {
          type: 'subscribe',
          id: subscriptionId,
          channel: channelPath,
          authorization: {
            host: httpHost,
            Authorization: idToken,
            authorization: idToken
          }
        };

        if (socket.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify(subscribeMsg));
        }
        return;
      }

      if (msg.type === 'subscribe_success') {
        gotSubscribeSuccess = true;
        log('subscribed OK', { id: subscriptionId });
        return;
      }

      if (msg.type === 'data' || msg.type === 'message') {
        const rawEvents: any[] = Array.isArray(msg.events)
          ? msg.events
          : Array.isArray(msg.event)
          ? msg.event
          : msg.event != null
          ? [msg.event]
          : [];
        rawEvents.forEach((rawEvt) => {
          let evt: any = rawEvt;
          if (typeof evt === 'string') {
            try {
              evt = JSON.parse(evt);
            } catch {}
          }
          params.onEvent(evt);
        });
        return;
      }

      if (msg.type === 'subscribe_error' || msg.type === 'connection_error') {
        const errors = Array.isArray(msg.errors) ? msg.errors : [];
        log(`${msg.type} details`, errors);
        safeError({ ...msg, errors });
        scheduleReconnect(msg.type, errors);
        return;
      }

      if (msg.type === 'ka') return;
    };

    socket.onerror = (e) => {
      if (ws !== socket || !alive) return;
      log('ws.onerror', e);
      safeError(e);
    };

    socket.onclose = (e) => {
      if (ws === socket) ws = null;
      log('closed', {
        code: e.code,
        reason: e.reason,
        wasClean: e.wasClean,
        mode: 'apiKey',
        gotConnectionAck,
        gotSubscribeSuccess
      });
      if (!alive) return;
      scheduleReconnect('socket_closed', {
        code: e.code,
        reason: e.reason,
        wasClean: e.wasClean
      });
    };
  };

  connect();

  // cleanup
  const cleanupFn = () => {
    alive = false;
    clearRetry();
    closeSocket('client_close');
  };
  return cleanupFn;
}
